// src/pages/ReportsPage.js
// Systems has been removed, along with the global /api/audits endpoint it
// used for compliance reports. Each of the three remaining types gets its
// own equivalent reports instead, built from that type's own list/get/audit
// endpoints — no report currently needs a parameter picker, so that
// machinery (SystemPicker/UserPicker) has been removed along with Systems.
import React, { useState } from "react";
import * as XLSX from "xlsx";
import { saasApplicationsApi, localApplicationsApi, sharedCredentialsApi } from "../utils/api";
import { auditStatus, formatDate } from "../utils/dates";

// ── Format helpers ─────────────────────────────────────────────────────────────
const fmt     = v => (v == null || v === "" ? "" : String(v));
const fmtDate = v => { if (!v) return ""; try { return new Date(v).toLocaleDateString("en-GB"); } catch { return fmt(v); } };
const fmtArr  = v => { if (!v) return ""; if (Array.isArray(v)) return v.join(", "); try { const p = JSON.parse(v); return Array.isArray(p) ? p.join(", ") : fmt(v); } catch { return fmt(v); } };
const fmtOwners = v => (v || []).map(o => o.displayName).join(", ");
const fmtStatus = v => v.replace("due-soon","Due Soon").replace("ok","Up to date").replace("overdue","Overdue").replace("unknown","Not set");

// ── PDF generation ────────────────────────────────────────────────────────────
async function generatePDF(sheets, title) {
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const PURPLE = [59, 0, 132];
  const LIGHT  = [245, 242, 252];

  sheets.forEach((sheet, idx) => {
    if (idx > 0) doc.addPage();
    const [header, ...rows] = sheet.rows;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...PURPLE);
    doc.text(idx === 0 ? title : sheet.name, 14, 16);

    if (sheets.length > 1) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      doc.text(`Sheet: ${sheet.name}`, 14, 22);
    }

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated ${new Date().toLocaleDateString("en-GB")}`, doc.internal.pageSize.width - 14, 16, { align: "right" });

    autoTable(doc, {
      head:       [header],
      body:       rows.map(r => r.map(fmt)),
      startY:     sheets.length > 1 ? 26 : 22,
      margin:     { left: 14, right: 14 },
      styles:     { fontSize: 7.5, cellPadding: 2.5, overflow: "linebreak" },
      headStyles: { fillColor: PURPLE, textColor: 255, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: LIGHT },
      tableLineColor: [220, 215, 235],
      tableLineWidth: 0.1,
      didDrawPage: (data) => {
        doc.setFontSize(7);
        doc.setTextColor(180, 180, 180);
        doc.text(
          `SUFC System Governance Portal — ${title} — Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          14, doc.internal.pageSize.height - 6
        );
      },
    });
  });

  return doc;
}

// ── XLSX helpers ──────────────────────────────────────────────────────────────
function buildWorkbook(sheets) {
  const wb = XLSX.utils.book_new();
  for (const { name, rows, cols } of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (cols) ws["!cols"] = cols.map(w => ({ wch: w }));
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
  }
  return wb;
}

function xlsxFilename(title) {
  const safe = title.replace(/[^a-z0-9]/gi, "_");
  return `SUFC_${safe}_${new Date().toISOString().slice(0,10)}.xlsx`;
}

function pdfFilename(title) {
  const safe = title.replace(/[^a-z0-9]/gi, "_");
  return `SUFC_${safe}_${new Date().toISOString().slice(0,10)}.pdf`;
}

// ── Report builders — one set per resource type ───────────────────────────────
function buildTypeReports(key, api, label) {
  return [
    {
      id: `${key}-all`, category: label, title: `All ${label}`,
      description: `Every registered ${label.toLowerCase()} with owner, category, status and next review date.`,
      params: [],
      async buildSheets() {
        const items = await api.list();
        const header = ["Name","Category","Access Owner(s)","Billing Owner(s)","Auth Type","Description","Status","Last Reviewed","Next Review Due","Interval (months)","Active"];
        const rows = items.map(s => [fmt(s.name),fmt(s.category),fmtOwners(s.accessOwners),fmtOwners(s.billingOwners),fmt(s.authType),fmt(s.description),fmtStatus(auditStatus(s.nextAuditDue)),fmtDate(s.lastAuditDate),fmtDate(s.nextAuditDue),fmt(s.auditIntervalMonths),s.active?"Yes":"No"]);
        return [{ name:`All ${label}`, rows:[header,...rows], cols:[26,14,26,26,16,36,14,12,12,8,5] }];
      },
    },
    {
      id: `${key}-users`, category: label, title: `Users per ${label.slice(0, -1)}`,
      description: `All active user assignments — one row per user per ${label.toLowerCase().slice(0, -1)}.`,
      params: [],
      async buildSheets() {
        const items = await api.list();
        const header = ["Name","Category","Access Owner(s)","User Name","User UPN","Access","Notes"];
        const rows = [];
        for (const item of items) {
          const data = await api.get(item.id).catch(() => null);
          if (!data) continue;
          for (const u of (data.users||[]).filter(u=>u.active)) {
            rows.push([fmt(item.name),fmt(item.category),fmtOwners(item.accessOwners),fmt(u.displayName),fmt(u.upn),fmtArr(u.accessLevel),fmt(u.notes)]);
          }
        }
        return [{ name:`Users per ${label}`, rows:[header,...rows], cols:[26,14,26,24,34,18,28] }];
      },
    },
    {
      id: `${key}-audits`, category: label, title: `Audit History — ${label}`,
      description: `All audit events across every registered ${label.toLowerCase()}, merged into one log.`,
      params: [],
      async buildSheets() {
        const items = await api.list();
        const header = ["Date","Name","Action","Auditor","Detail"];
        const rows = [];
        for (const item of items) {
          const res = await fetch(`/api/${key}/${item.id}/audits`).catch(() => null);
          const data = res && res.ok ? await res.json().catch(() => []) : [];
          for (const a of (Array.isArray(data) ? data : [])) {
            rows.push([fmtDate(a.timestamp),fmt(item.name),fmt(a.action),fmt(a.auditedByName),fmt(a.detail)]);
          }
        }
        rows.sort((a,b) => new Date(b[0]) - new Date(a[0]));
        return [{ name:`${label} Audit`, rows:[header,...rows], cols:[12,26,20,24,55] }];
      },
    },
  ];
}

const REPORTS = [
  ...buildTypeReports("saas-applications",  saasApplicationsApi,  "SaaS Applications"),
  ...buildTypeReports("local-applications", localApplicationsApi, "Local Applications"),
  ...buildTypeReports("shared-credentials", sharedCredentialsApi, "Shared Credentials"),
];

const CATEGORIES = ["SaaS Applications", "Local Applications", "Shared Credentials"];

// ── Report card ───────────────────────────────────────────────────────────────
function ReportCard({ report }) {
  const [running, setRunning] = useState(null); // null | "xlsx" | "pdf"
  const [error,   setError]   = useState("");

  const run = async (format) => {
    setRunning(format); setError("");
    try {
      const sheets = await report.buildSheets();

      if (format === "xlsx") {
        const wb = buildWorkbook(sheets);
        XLSX.writeFile(wb, xlsxFilename(report.title));
      } else {
        const doc = await generatePDF(sheets, report.title);
        doc.save(pdfFilename(report.title));
      }
    } catch (err) {
      setError("Export failed: " + err.message);
      console.error(err);
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="card" style={{ padding:"16px 20px" }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{report.title}</div>
          <div style={{ fontSize:12, color:"var(--gray-500)" }}>{report.description}</div>
          {error && <div style={{ fontSize:12, color:"var(--red)", marginTop:6 }}>{error}</div>}
        </div>

        {/* Export buttons */}
        <div style={{ display:"flex", gap:6, flexShrink:0, alignItems:"flex-start" }}>
          <button className="btn-secondary" style={{ fontSize:12, padding:"6px 12px", whiteSpace:"nowrap" }}
            onClick={()=>run("pdf")} disabled={!!running}>
            {running==="pdf"
              ? <><span className="spinner" style={{width:12,height:12}} /> Generating…</>
              : "⬇ PDF"}
          </button>
          <button className="btn-primary" style={{ fontSize:12, padding:"6px 12px", whiteSpace:"nowrap" }}
            onClick={()=>run("xlsx")} disabled={!!running}>
            {running==="xlsx"
              ? <><span className="spinner" style={{width:12,height:12}} /> Generating…</>
              : "⬇ Excel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Reports</div>
          <div className="page-subtitle">Export portal data to Excel or PDF. Generated in your browser from live data.</div>
        </div>
      </div>

      {CATEGORIES.map(cat => {
        const catReports = REPORTS.filter(r => r.category === cat);
        return (
          <div key={cat} style={{ marginBottom:28 }}>
            <h2 style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-500)", marginBottom:10 }}>{cat}</h2>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {catReports.map(r => <ReportCard key={r.id} report={r} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
